/* -------------------------------------------------------------------------- */
/* Design Schema API — all schema + cover page + export endpoints            */
/* -------------------------------------------------------------------------- */

import { apiV1 } from "api-v2";
import type {
	DesignSchemaResponse,
	DesignSchemaUpdatePayload,
	ResolvedDesignSchema,
	CoverPageUploadResponse,
} from "../types/DesignSchema.type";

const unwrap = <T>(responseData: unknown): T => {
	if (responseData && typeof responseData === "object" && "data" in responseData) {
		return (responseData as Record<string, unknown>).data as T;
	}
	return responseData as T;
};

const BASE = (companyId: number) => `companies/${companyId}/design-schema`;

/* ---- Global schema ---- */

export const getDesignSchema = async (companyId: number): Promise<DesignSchemaResponse> => {
	const response = await apiV1.get(BASE(companyId));
	return unwrap<DesignSchemaResponse>(response.data);
};

export const saveDesignSchema = async (
	companyId: number,
	payload: DesignSchemaUpdatePayload
): Promise<DesignSchemaResponse> => {
	const response = await apiV1.put(BASE(companyId), payload);
	return unwrap<DesignSchemaResponse>(response.data);
};

/* ---- Override schema ---- */

export const getDesignSchemaOverride = async (
	companyId: number,
	reportType: string
): Promise<DesignSchemaResponse> => {
	const response = await apiV1.get(`${BASE(companyId)}/${reportType}`);
	return unwrap<DesignSchemaResponse>(response.data);
};

export const saveDesignSchemaOverride = async (
	companyId: number,
	reportType: string,
	payload: DesignSchemaUpdatePayload
): Promise<DesignSchemaResponse> => {
	const response = await apiV1.put(`${BASE(companyId)}/${reportType}`, payload);
	return unwrap<DesignSchemaResponse>(response.data);
};

export const deleteDesignSchemaOverride = async (
	companyId: number,
	reportType: string
): Promise<void> => {
	await apiV1.delete(`${BASE(companyId)}/${reportType}`);
};

/* ---- Resolved schema ---- */

export const getResolvedSchema = async (
	companyId: number,
	reportType: string
): Promise<ResolvedDesignSchema> => {
	const response = await apiV1.get(`${BASE(companyId)}/${reportType}/resolved`);
	return unwrap<ResolvedDesignSchema>(response.data);
};

/* ---- Cover page ---- */

export const uploadCoverPage = async (
	companyId: number,
	file: File,
	reportType?: string
): Promise<CoverPageUploadResponse> => {
	const formData = new FormData();
	formData.append("cover_page", file);
	const url = reportType
		? `${BASE(companyId)}/${reportType}/cover-page`
		: `${BASE(companyId)}/cover-page`;
	const response = await apiV1.post(url, formData, {
		headers: { "Content-Type": "multipart/form-data" },
	});
	return unwrap<CoverPageUploadResponse>(response.data);
};

export const deleteCoverPage = async (
	companyId: number,
	reportType?: string
): Promise<void> => {
	const url = reportType
		? `${BASE(companyId)}/${reportType}/cover-page`
		: `${BASE(companyId)}/cover-page`;
	await apiV1.delete(url);
};

/* ---- Live preview HTML ---- */

export interface PreviewPageHtml {
	html: string;
	label: string;
}

export interface PreviewHtmlResponse {
	cover_html: string;
	design_schema: Record<string, unknown>;
	footer_html: string | null;
	header_html: string | null;
	pages: PreviewPageHtml[];
}

export const getPreviewHtml = async (
	companyId: number,
	payload: Record<string, unknown>
): Promise<PreviewHtmlResponse> => {
	// Support both legacy (schema-only) and new (full payload with design_schema key) formats
	const body = payload.design_schema ? payload : { design_schema: payload };
	const response = await apiV1.post("reports/shortlist/preview-html", body);
	return unwrap<PreviewHtmlResponse>(response.data);
};

/* ---- PDF preview/export ---- */

export interface PdfDownloadResponse {
	download_url: string;
	filename: string;
}

export const previewPdf = async (
	companyId: number,
	schema: DesignSchemaUpdatePayload
): Promise<PdfDownloadResponse> => {
	const response = await apiV1.post(
		"reports/shortlist/preview",
		{ design_schema: schema }
	);
	return unwrap<PdfDownloadResponse>(response.data);
};

export interface ExportPdfOptions {
	additionalText?: string;
	companyIds: number[];
	coverCreatedByRole?: string;
	coverPageFileId?: number | null;
	coverPageMode?: "default" | "none" | "upload";
	coverPresentedTo?: string;
	coverPresentedToRole?: string;
	coverProjectName?: string;
	coverShowLogo?: boolean | null;
	coverSupplierRole?: string;
	groupId?: number | string;
	includeProjectDetails?: boolean;
	projectDescriptionOverride?: string;
	projectId?: number | null;
	projectVisibleFields?: Record<string, boolean>;
	searchSectorIds?: number[];
	title?: string;
	visibleFields?: Record<string, Record<string, boolean>>;
	visibleSections?: Record<string, boolean>;
}

export const exportPdf = async (
	companyId: number,
	companyIds: number[],
	title?: string,
	searchSectorIds?: number[],
	groupId?: number | string,
	options?: Partial<ExportPdfOptions>
): Promise<PdfDownloadResponse> => {
	const payload: Record<string, unknown> = { company_ids: companyIds };
	if (title) {
		payload.title = title;
	}
	if (searchSectorIds && searchSectorIds.length > 0) {
		payload.search_sector_ids = searchSectorIds;
	}
	if (groupId) {
		payload.group_id = groupId;
	}
	if (options?.visibleSections) {
		payload.visible_sections = options.visibleSections;
	}
	if (options?.visibleFields) {
		payload.visible_fields = options.visibleFields;
	}
	if (options?.projectId) {
		payload.project_id = options.projectId;
	}
	if (options?.projectDescriptionOverride) {
		payload.project_description_override = options.projectDescriptionOverride;
	}
	if (options?.additionalText) {
		payload.additional_text = options.additionalText;
	}
	if (options?.coverPageFileId) {
		payload.cover_page_file_id = options.coverPageFileId;
	}
	if (options?.coverPageMode) {
		payload.cover_page_mode = options.coverPageMode;
	}
	if (options?.includeProjectDetails !== undefined) {
		payload.include_project_details = options.includeProjectDetails;
	}
	if (options?.projectVisibleFields) {
		payload.project_visible_fields = options.projectVisibleFields;
	}
	if (options?.coverProjectName) {
		payload.cover_project_name = options.coverProjectName;
	}
	if (options?.coverSupplierRole) {
		payload.cover_supplier_role = options.coverSupplierRole;
	}
	if (options?.coverPresentedTo) {
		payload.cover_presented_to = options.coverPresentedTo;
	}
	if (options?.coverPresentedToRole) {
		payload.cover_presented_to_role = options.coverPresentedToRole;
	}
	if (options?.coverShowLogo !== undefined && options?.coverShowLogo !== null) {
		payload.cover_show_logo = options.coverShowLogo;
	}
	if (options?.coverCreatedByRole) {
		payload.cover_created_by_role = options.coverCreatedByRole;
	}
	const response = await apiV1.post("reports/shortlist/export", payload);
	return unwrap<PdfDownloadResponse>(response.data);
};
